import { getApiBaseUrl } from "../config/env";
import { isAdminAuthLive } from "./adminAuth";

function apiPath(path) {
  const base = getApiBaseUrl();
  if (!base) return path;
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

/**
 * @returns {Promise<{ sesConfigured: boolean; invitationSettings?: { invitationAutoSendOnInvite: boolean } }>}
 */
export async function fetchAdminEmailStatus() {
  if (!isAdminAuthLive()) {
    return { sesConfigured: false, invitationSettings: { invitationAutoSendOnInvite: false } };
  }
  const res = await fetch(apiPath("/api/admin/email/status"), {
    method: "GET",
    credentials: "include",
  });
  if (!res.ok) {
    return { sesConfigured: false, invitationSettings: { invitationAutoSendOnInvite: false } };
  }
  return res.json();
}

/**
 * @param {boolean} invitationAutoSendOnInvite
 */
export async function putAdminInvitationSettings(invitationAutoSendOnInvite) {
  if (!isAdminAuthLive()) {
    throw new Error("Invitation settings require the live admin API.");
  }
  const res = await fetch(apiPath("/api/admin/email/invitation-settings"), {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ invitationAutoSendOnInvite: Boolean(invitationAutoSendOnInvite) }),
  });
  if (!res.ok) {
    let message = "Could not save invitation settings.";
    try {
      const data = await res.json();
      if (typeof data.message === "string") message = data.message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return res.json();
}

/**
 * @param {{ force?: boolean }} [opts]
 */
export async function postAdminBulkInvitationEmails(opts = {}) {
  if (!isAdminAuthLive()) {
    throw new Error("Bulk send requires the live admin API.");
  }
  const res = await fetch(apiPath("/api/admin/email/invitations/send-bulk"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ force: opts.force === true }),
  });
  if (!res.ok) {
    let message = "Could not send bulk invitations.";
    try {
      const data = await res.json();
      if (typeof data.message === "string") message = data.message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return res.json();
}

/**
 * @param {string} personId
 * @param {{ force?: boolean }} [opts]
 */
export async function postAdminSendPersonInvitation(personId, opts = {}) {
  if (!isAdminAuthLive()) {
    throw new Error("Send invitation requires the live admin API.");
  }
  const res = await fetch(
    apiPath(`/api/admin/email/persons/${encodeURIComponent(personId)}/invitation/send`),
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ force: opts.force === true }),
    }
  );
  if (!res.ok) {
    let message = "Could not send invitation.";
    try {
      const data = await res.json();
      if (typeof data.message === "string") message = data.message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return res.json();
}

/**
 * @param {string} personId
 */
export async function postAdminResetPersonInvitationEmail(personId) {
  if (!isAdminAuthLive()) {
    throw new Error("Reset requires the live admin API.");
  }
  const res = await fetch(
    apiPath(`/api/admin/email/persons/${encodeURIComponent(personId)}/invitation/reset-state`),
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }
  );
  if (!res.ok) {
    let message = "Could not reset invitation state.";
    try {
      const data = await res.json();
      if (typeof data.message === "string") message = data.message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return res.json();
}

/**
 * @returns {Promise<{ subject: string; textBody: string; htmlBody: string }>}
 */
export async function fetchAdminEmailPreview() {
  if (!isAdminAuthLive()) {
    throw new Error("Preview requires the live admin API.");
  }
  const res = await fetch(apiPath("/api/admin/email/preview"), {
    method: "GET",
    credentials: "include",
  });
  if (!res.ok) {
    let message = "Could not load preview.";
    try {
      const data = await res.json();
      if (typeof data.message === "string") message = data.message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return res.json();
}

/**
 * @param {string} to
 * @returns {Promise<{ ok: boolean; messageId: string; to: string; greetingPreview?: { recipientName: string } }>}
 */
export async function postAdminSendTestEmail(to) {
  if (!isAdminAuthLive()) {
    await new Promise((r) => setTimeout(r, 250));
    return { ok: true, messageId: "dev-local", to: String(to).trim(), dev: true };
  }

  const res = await fetch(apiPath("/api/admin/email/send-test"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ to: String(to).trim() }),
  });
  if (!res.ok) {
    let message = "Could not send test email.";
    try {
      const data = await res.json();
      if (typeof data.message === "string") message = data.message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return res.json();
}
